export type PublicSeoLanguage = 'en' | 'fr';

export const PUBLIC_INDEXABLE_PAGE_PATHS = [
  '/',
  '/manifesto',
  '/assistant-ia-adr',
  '/agent-consultant-adr',
  '/agent-auditeur-transport',
  '/eliminateurs-dechets',
  '/fiabilite-ia-adr',
  '/iso270001',
] as const;

export const PUBLIC_LOCALIZED_PAGE_PATHS = [
  ...PUBLIC_INDEXABLE_PAGE_PATHS,
  '/terms',
  '/privacy',
  '/cookies',
] as const;

export const PUBLIC_GENERIC_SEO_PAGE_PATHS = [
  '/',
  '/manifesto',
  '/terms',
  '/privacy',
  '/cookies',
] as const;

export const PUBLIC_LEGAL_PAGE_PATHS = ['/terms', '/privacy', '/cookies'] as const;

export type PublicIndexablePagePath = typeof PUBLIC_INDEXABLE_PAGE_PATHS[number];
export type PublicLocalizedPagePath = typeof PUBLIC_LOCALIZED_PAGE_PATHS[number];
export type PublicGenericSeoPagePath = typeof PUBLIC_GENERIC_SEO_PAGE_PATHS[number];
export type PublicLegalPagePath = typeof PUBLIC_LEGAL_PAGE_PATHS[number];

const ENGLISH_PAGE_PATHS: Partial<Record<PublicLocalizedPagePath, string>> = {
  '/eliminateurs-dechets': '/waste-management',
};

type QuestionExample = { question: string; slug: string };
type LocalizedSeo = { title: string; description: string; imageAlt: string };

const ORIGIN = 'https://palen-adr.eu';
const IMAGE = `${ORIGIN}/assets/logo/palen_social_card_20260710.png`;
const LOGO = `${ORIGIN}/assets/logo/palen_public.png`;
export const PUBLIC_SEO_HEAD_START = '<!-- PALEN_SEO_HEAD_START -->';
export const PUBLIC_SEO_HEAD_END = '<!-- PALEN_SEO_HEAD_END -->';

const SEO_BY_PATH: Record<PublicGenericSeoPagePath, Record<PublicSeoLanguage, LocalizedSeo>> = {
  '/': {
    fr: {
      title: 'Palen - IA pour les CSTMD et professionnels du transport ADR',
      description: 'Palen aide les CSTMD et professionnels du transport ADR avec des réponses réglementaires fiables, couvrant l’ADR et ses textes nationaux en France, en Allemagne et en Italie.',
      imageAlt: 'Palen - IA dediee aux professionnels du transport ADR',
    },
    en: {
      title: 'Palen - ADR AI for dangerous goods professionals',
      description: 'Palen helps dangerous goods safety advisers and ADR professionals obtain reliable regulatory answers covering ADR and national rules in France, Germany and Italy.',
      imageAlt: 'Palen ADR AI for dangerous goods professionals',
    },
  },
  '/manifesto': {
    fr: {
      title: 'L’IA doit donner un temps d’avance à l’expertise.',
      description: 'Le manifeste Palen pour une IA d’entreprise sourcée, vérifiable et placée sous responsabilité humaine dans les usages professionnels de l’ADR.',
      imageAlt: 'Manifeste Palen pour une intelligence artificielle responsable dans l’ADR',
    },
    en: {
      title: 'AI should keep professional expertise one step ahead.',
      description: 'The Palen manifesto for sourced, verifiable enterprise AI kept under human responsibility in professional ADR work.',
      imageAlt: 'Palen manifesto for responsible artificial intelligence in ADR',
    },
  },
  '/terms': {
    fr: { title: 'Conditions d’utilisation | Palen', description: 'Les conditions d’utilisation du service Palen.', imageAlt: 'Palen' },
    en: { title: 'Terms of Service | Palen', description: 'The terms governing use of the Palen service.', imageAlt: 'Palen' },
  },
  '/privacy': {
    fr: { title: 'Politique de confidentialité | Palen', description: 'La politique de confidentialité du service Palen.', imageAlt: 'Palen' },
    en: { title: 'Privacy Policy | Palen', description: 'How Palen processes and protects personal data.', imageAlt: 'Palen' },
  },
  '/cookies': {
    fr: { title: 'Politique des cookies | Palen', description: 'La politique des cookies du service Palen.', imageAlt: 'Palen' },
    en: { title: 'Cookie Policy | Palen', description: 'How Palen uses cookies and similar browser storage.', imageAlt: 'Palen' },
  },
};

export function localizePublicSeoPath(path: PublicLocalizedPagePath, language: PublicSeoLanguage): string {
  if (language === 'fr') return path;
  return path === '/' ? '/en/' : `/en${ENGLISH_PAGE_PATHS[path] ?? path}`;
}

export function resolvePublicSeoPagePath(pathname: string, language: PublicSeoLanguage): PublicLocalizedPagePath | null {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  return PUBLIC_LOCALIZED_PAGE_PATHS.find((path) => (
    (localizePublicSeoPath(path, language).replace(/\/+$/, '') || '/') === normalizedPathname
  )) ?? null;
}

export function isPublicIndexablePagePath(path: string): path is PublicIndexablePagePath {
  return (PUBLIC_INDEXABLE_PAGE_PATHS as readonly string[]).includes(path);
}

export function isPublicGenericSeoPagePath(path: string): path is PublicGenericSeoPagePath {
  return (PUBLIC_GENERIC_SEO_PAGE_PATHS as readonly string[]).includes(path);
}

export function buildPublicSeoHeadMarkup(
  path: PublicGenericSeoPagePath,
  language: PublicSeoLanguage = 'fr',
  questionExamples: QuestionExample[] = [],
): string {
  const seo = SEO_BY_PATH[path][language];
  const url = absoluteLocalizedUrl(path, language);
  const structuredData = path === '/'
    ? buildLandingStructuredData(questionExamples, language)
    : path === '/manifesto'
      ? buildManifestoStructuredData(language)
      : buildLegalStructuredData(path, language);
  const preload = path === '/' || path === '/manifesto'
    ? `<link
      rel="preload"
      as="image"
      href="/assets/logo/palen_platform-180.webp?v=20260807"
      imagesrcset="/assets/logo/palen_platform-180.webp?v=20260807 180w, /assets/logo/palen_platform-280.webp?v=20260807 280w, /assets/logo/palen_platform-360.webp?v=20260807 360w"
      imagesizes="(min-width: 640px) 224px, 136px"
      type="image/webp"
      fetchpriority="high"
    />`
    : '';
  const robots = (PUBLIC_LEGAL_PAGE_PATHS as readonly string[]).includes(path)
    ? 'noindex,follow'
    : 'index,follow,max-image-preview:large';

  return `
    ${PUBLIC_SEO_HEAD_START}
    <title>${seo.title}</title>
    <meta name="description" content="${seo.description}" />
    <meta name="robots" content="${robots}" />
    <link rel="canonical" href="${url}" />
    ${alternateLinks(path)}
    ${preload}
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="Palen" />
    <meta property="og:title" content="${seo.title}" />
    <meta property="og:description" content="${seo.description}" />
    <meta property="og:url" content="${url}" />
    <meta property="og:image" content="${IMAGE}" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="627" />
    <meta property="og:image:alt" content="${seo.imageAlt}" />
    <meta property="og:locale" content="${language === 'en' ? 'en_US' : 'fr_FR'}" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content="${seo.title}" />
    <meta name="twitter:description" content="${seo.description}" />
    <meta name="twitter:image" content="${IMAGE}" />
    <meta name="twitter:image:alt" content="${seo.imageAlt}" />
    <script type="application/ld+json">${serializeStructuredData(structuredData)}</script>
    ${PUBLIC_SEO_HEAD_END}`;
}

function alternateLinks(path: PublicGenericSeoPagePath): string {
  return [
    `<link rel="alternate" hreflang="fr" href="${absoluteLocalizedUrl(path, 'fr')}" />`,
    `<link rel="alternate" hreflang="en" href="${absoluteLocalizedUrl(path, 'en')}" />`,
    `<link rel="alternate" hreflang="x-default" href="${absoluteLocalizedUrl(path, 'fr')}" />`,
  ].join('\n    ');
}

function absoluteLocalizedUrl(path: PublicLocalizedPagePath, language: PublicSeoLanguage): string {
  return new URL(localizePublicSeoPath(path, language), `${ORIGIN}/`).toString();
}

function websiteUrl(language: PublicSeoLanguage): string {
  return language === 'en' ? `${ORIGIN}/en/` : `${ORIGIN}/`;
}

function organization() {
  return { '@type': 'Organization', '@id': `${ORIGIN}/#organization`, name: 'Palen', url: `${ORIGIN}/`, logo: LOGO };
}

function website(language: PublicSeoLanguage) {
  const url = websiteUrl(language);
  return { '@type': 'WebSite', '@id': `${url}#website`, name: 'Palen', url, inLanguage: language, publisher: { '@id': `${ORIGIN}/#organization` } };
}

function buildLandingStructuredData(questionExamples: QuestionExample[], language: PublicSeoLanguage) {
  const url = websiteUrl(language);
  const graph: Record<string, unknown>[] = [
    organization(),
    website(language),
    {
      '@type': 'SoftwareApplication',
      '@id': `${url}#application`,
      name: 'Palen',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url,
      inLanguage: language,
      description: language === 'en'
        ? 'Specialized ADR artificial intelligence for dangerous goods professionals, providing clear, sourced regulatory answers.'
        : 'IA spécialisée en Transport de Marchandises Dangereuses par Route (ADR) pour CSTMD : réponses réglementaires claires, sourcées et rapides.',
      publisher: { '@id': `${ORIGIN}/#organization` },
      keywords: language === 'en'
        ? ['ADR', 'dangerous goods transport', 'ADR regulations', 'dangerous goods safety adviser', 'DGSA']
        : ['ADR', 'transport de marchandises dangereuses', 'réglementation ADR', 'conseiller sécurité ADR', 'CSTMD'],
    },
  ];

  if (questionExamples.length > 0) {
    graph.push({
      '@type': 'ItemList',
      '@id': `${url}#shared-question-examples`,
      name: language === 'en' ? 'ADR question examples' : 'Exemples de questions ADR',
      itemListElement: questionExamples.map((example, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: example.question,
        url: `${ORIGIN}/adr-case/${encodeURIComponent(example.slug)}`,
      })),
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

function buildManifestoStructuredData(language: PublicSeoLanguage) {
  const seo = SEO_BY_PATH['/manifesto'][language];
  const url = absoluteLocalizedUrl('/manifesto', language);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization(),
      website(language),
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        name: seo.title,
        url,
        description: seo.description,
        inLanguage: language,
        isPartOf: { '@id': `${websiteUrl(language)}#website` },
        publisher: { '@id': `${ORIGIN}/#organization` },
        about: language === 'en'
          ? ['Responsible artificial intelligence', 'Enterprise artificial intelligence', 'Dangerous goods transport', 'ADR regulations']
          : ['Intelligence artificielle responsable', 'Intelligence artificielle en entreprise', 'Transport de marchandises dangereuses', 'Réglementation ADR'],
      },
    ],
  };
}

function buildLegalStructuredData(path: PublicLegalPagePath, language: PublicSeoLanguage) {
  const seo = SEO_BY_PATH[path][language];
  const url = absoluteLocalizedUrl(path, language);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    name: seo.title,
    url,
    description: seo.description,
    inLanguage: language,
    isPartOf: { '@id': `${websiteUrl(language)}#website` },
    publisher: { '@id': `${ORIGIN}/#organization` },
  };
}

function serializeStructuredData(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}
